import React, { useEffect, useState, useCallback } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Autocomplete,
  TextField,
  Chip,
  Menu,
  MenuItem,
  Box,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Tooltip,
  Drawer,
  useMediaQuery,
  useTheme,
  Divider,
  Button,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import MenuIcon from '@mui/icons-material/Menu';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import PersonIcon from '@mui/icons-material/Person';
import GridViewIcon from '@mui/icons-material/GridView';
import PrintIcon from '@mui/icons-material/Print';
import Inventory2Icon from '@mui/icons-material/Inventory2'; // 🖥️ Plan 2 — Kiosk Urunleri menu item
import LoginIcon from '@mui/icons-material/Login';
import { useNavigate } from 'react-router-dom';
import { PersonnelPermissions } from './modals/QuickSaleScreenSettingsModal';
import * as signalR from '@microsoft/signalr';
import signalRService from '../../../../services/signalRService';
import { useTranslation } from 'react-i18next';
import { APP_VERSION } from '../../../../version';
import AnnouncementMarquee from './AnnouncementMarquee';
import type { QuickSaleAnnouncementDto } from '../../../../services/announcementApiService';

// QuickSaleScreen.tsx'den alınacak tipler (veya ortak bir types.ts dosyasından)
interface QuickSaleScreenData {
  id: string;
  name: string;
}

// PYK Ekranı Verisi (PykScreenView.tsx'den alınabilir)
interface PykScreenData {
  id: string;
  name: string;
  branchId?: string | null;
  branchName?: string;
}

// Birleştirilmiş ekran verisi
interface CombinedScreenOption {
  id: string;
  name: string;
  type: 'quicksale' | 'pyk';
  branchId?: string | null;
  branchName?: string;
}

// Kullanıcı Bilgileri
interface UserDetails {
  uid?: string;
  role?: string;
  branchesId?: string | null;
  email?: string;
  firstName?: string;
  lastName?: string;
}

// UserData artık UserDetails ile aynı, bu sayede QuickSaleScreen'den gelen veri doğrudan kullanılabilir
type UserData = UserDetails;

// ✅ YENİ: Yazarkasa bağlantı durumu tipi
export type FiscalDeviceStatus = 'connected' | 'disconnected' | 'checking' | 'not_configured';

interface QuickSaleAppBarProps {
  userDetails: UserData | null;
  permittedScreens: QuickSaleScreenData[];
  currentScreen: QuickSaleScreenData | null;
  handleOpenUISettingsModal?: () => void;
  userFullName?: string;
  handleOpenUserMenu?: (event: React.MouseEvent<HTMLElement>) => void;
  handleLogout?: () => void;
  anchorElUserMenu?: null | HTMLElement;
  handleCloseUserMenu?: () => void;
  lastSettingsRefresh?: string | null;
  title?: string;
  permittedPykScreens?: PykScreenData[];
  handleGoToTaskBoard?: () => void;
  currentUserPermissions?: PersonnelPermissions | null;
  onOpenBulkAddAccountModal?: () => void;
  companyId?: string | null; // ✅ YENİ: SignalR için
  useKitchenScreen?: boolean; // ✅ YENİ: Mutfak ekranı entegrasyonu açık mı?
  isRestaurantMode?: boolean; // ✅ YENİ: Restaurant modunda mı?
  tableName?: string; // ✅ YENİ: Masa adı
  waiterName?: string; // ✅ YENİ: Garson adı
  // ✅ YENİ: Yazarkasa bağlantı durumu
  fiscalDeviceStatus?: FiscalDeviceStatus;
  selectedFiscalDevice?: 'hugin_t300' | 'inpos_m530' | 'beko_x30tr' | 'beko_300tr' | 'pavopay' | 'ingenico_5000f' | null;
  useFiscalDeviceForCash?: boolean; // ÖKC kullanımı aktif mi?
  // ✅ YENİ: Rezerve edilmiş sipariş numarası (sağ üstte gösterilir)
  reservedShortCode?: string | null;
  // ✅ YENİ: Geri butonuna basıldığında çağrılacak callback (Restaurant modunda yazdırılmamış ürün kontrolü için)
  onBackClick?: () => void;
  // ✅ YENİ: Kasiyer sistemi
  useCashierSystem?: boolean; // Kasiyer sistemi aktif mi?
  currentCashierName?: string | null; // Giriş yapmış kasiyer/garson adı
  loginType?: 'waiter' | 'cashier'; // ✅ YENİ: Giriş tipi (garson mu kasiyer mi?)
  onOpenCashierLogin?: () => void; // Kasiyer giriş modalını aç
  onCashierLogout?: () => void; // Kasiyer çıkış yap
  isPackageMode?: boolean; // ✅ YENİ: Paket sipariş modu (ekran seçimi gizlenir)
  onOpenKitchenOrdersModal?: () => void; // ✅ YENİ: Mutfak siparişleri modalını aç
  onOpenProductSortSettings?: () => void; // Ürün kartı sıralama düzeni
  onOpenEndOfDayPrint?: () => void; // ✅ YENİ: Gün Sonu yazdırma modalı (yetkili kasiyere özel)
  // ✨ YENİ: Aktif duyurular (header'da kayan yazı)
  announcements?: QuickSaleAnnouncementDto[];
  // 🖥️ Plan 2 — Kiosk Urunleri modal'ini ac
  onOpenKioskProducts?: () => void;
}

const QuickSaleAppBar: React.FC<QuickSaleAppBarProps> = ({
  userDetails,
  permittedScreens,
  currentScreen,
  isRestaurantMode = false, // ✅ YENİ
  tableName = '', // ✅ YENİ
  waiterName = '', // ✅ YENİ
  handleOpenUISettingsModal,
  userFullName,
  handleOpenUserMenu,
  handleLogout,
  anchorElUserMenu,
  handleCloseUserMenu,
  lastSettingsRefresh = null,
  title,
  permittedPykScreens = [],
  handleGoToTaskBoard,
  currentUserPermissions,
  onOpenBulkAddAccountModal,
  companyId, // ✅ YENİ
  useKitchenScreen = false, // ✅ YENİ
  // ✅ YENİ: Yazarkasa bağlantı durumu
  fiscalDeviceStatus = 'not_configured',
  selectedFiscalDevice = null,
  useFiscalDeviceForCash = false,
  // ✅ YENİ: Rezerve edilmiş sipariş numarası
  reservedShortCode = null,
  // ✅ YENİ: Geri butonu callback
  onBackClick,
  // ✅ YENİ: Kasiyer sistemi
  useCashierSystem = false,
  currentCashierName = null,
  loginType, // ✅ YENİ: 'waiter' | 'cashier'
  onOpenCashierLogin,
  onCashierLogout,
  isPackageMode = false, // ✅ YENİ: Paket sipariş modu
  onOpenKitchenOrdersModal, // ✅ YENİ: Mutfak siparişleri modalını aç
  onOpenProductSortSettings,
  onOpenEndOfDayPrint, // ✅ YENİ: Gün Sonu modalı
  announcements = [], // ✨ YENİ: Aktif duyurular
  onOpenKioskProducts, // 🖥️ Plan 2 — Kiosk Urunleri modali
}) => {
  const { t } = useTranslation('salesSystem');
  const navigate = useNavigate();
  const displayTitle = title ?? t('quickSaleAppBar.fastSaleTitle');
  
  // Mobil kontrolü
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Mobil menü drawer state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // ✅ YENİ: SignalR bağlantı durumu
  const [signalRStatus, setSignalRStatus] = useState<signalR.HubConnectionState>(
    signalR.HubConnectionState.Disconnected
  );
  
  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Fullscreen toggle fonksiyonu (Electron IPC fallback ile)
  const toggleFullscreen = useCallback(() => {
    // Electron ortamında IPC üzerinden fullscreen yap
    const electronAPI = (window as any).electronAPI;
    if (electronAPI?.send) {
      electronAPI.send('toggle-fullscreen');
      return;
    }
    
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.error('Tam ekran modu etkinleştirilemedi:', err);
      });
    } else {
      document.exitFullscreen();
    }
  }, []);

  // Fullscreen değişikliklerini dinle
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    // Electron IPC üzerinden fullscreen state değişikliğini dinle
    const handleElectronFullscreenChange = (e: any) => {
      setIsFullscreen(e.detail?.isFullScreen ?? false);
    };

    // F11 tuşu desteği
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'F11') {
        event.preventDefault();
        toggleFullscreen();
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    window.addEventListener('electron-fullscreen-change', handleElectronFullscreenChange);
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      window.removeEventListener('electron-fullscreen-change', handleElectronFullscreenChange);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [toggleFullscreen]);
  
  const [loadingPykScreens, setLoadingPykScreens] = useState<boolean>(false);
  const [allScreenOptions, setAllScreenOptions] = useState<CombinedScreenOption[]>([]);
  const [selectedScreenOption, setSelectedScreenOption] = useState<CombinedScreenOption | null>(null);

  // Mevcut URL'den hangi ekran tipinde olduğumuzu belirleme
  useEffect(() => {
    const pathname = window.location.pathname;
    
    // Hızlı satış ekranındayız ve ekran seçili
    if (pathname.includes('/hizli-satis/ekran/') && currentScreen) {
      setSelectedScreenOption({
        id: currentScreen.id,
        name: currentScreen.name,
        type: 'quicksale'
      });
    }
  }, [currentScreen]);

  // PYK Ekranlarını yükle
  useEffect(() => {
    if (!userDetails?.uid) return;

    const loadPykScreens = async () => {
      setLoadingPykScreens(true);
      try {
        // Mevcut URL'den hangi PYK ekranında olduğumuzu kontrol et
        const pathname = window.location.pathname;
        if (pathname.includes('/pyk-ekrani/')) {
          const pykScreenId = pathname.split('/pyk-ekrani/')[1];
          const currentPykScreen = permittedPykScreens.find(screen => screen.id === pykScreenId);
          if (currentPykScreen) {
            setSelectedScreenOption({
              id: currentPykScreen.id,
              name: currentPykScreen.name,
              type: 'pyk',
              branchId: currentPykScreen.branchId,
              branchName: currentPykScreen.branchName
            });
          }
        }
      } catch (error) {
        console.error("PYK ekranı URL kontrolü yapılırken hata:", error);
      } finally {
        setLoadingPykScreens(false);
      }
    };
    
    loadPykScreens();
  }, [userDetails?.uid, permittedPykScreens]);

  // ✅ YENİ: SignalR bağlantısını başlat ve durumu dinle (SADECE Mutfak Ekranı Entegrasyonu açıksa)
  useEffect(() => {
    // Mutfak ekranı kapalıysa bağlanma
    if (!useKitchenScreen) {
      console.log('[QuickSaleAppBar] 🔴 Mutfak ekranı kapalı, SignalR başlatılmıyor');
      setSignalRStatus(signalR.HubConnectionState.Disconnected);
      return;
    }

    if (!companyId) return;

    console.log('[QuickSaleAppBar] 🔌 SignalR başlatılıyor (Mutfak Ekranı Açık), CompanyID:', companyId);

    // SignalR bağlantısını başlat
    signalRService.startConnection(companyId).catch(error => {
      console.error('[QuickSaleAppBar] ❌ SignalR başlatılamadı:', error);
    });

    // Durum değişikliklerini dinle
    const handleStatusChange = (status: signalR.HubConnectionState) => {
      console.log('[QuickSaleAppBar] 📡 SignalR durumu değişti:', status);
      setSignalRStatus(status);
    };

    signalRService.onStatusChange(handleStatusChange);

    // Cleanup: Bağlantıyı KAPATMA - çünkü diğer ekranlarda da kullanılabilir
    return () => {
      console.log('[QuickSaleAppBar] 🔴 Component unmount - SignalR bağlantısı KESİLMİYOR (paylaşımlı)');
      // signalRService.stopConnection(); // Bağlantıyı kapatmıyoruz - paylaşımlı
    };
  }, [companyId, useKitchenScreen]); // ✅ useKitchenScreen dependency eklendi

  // Tüm ekran seçeneklerini birleştir
  useEffect(() => {
    const allOptions: CombinedScreenOption[] = [];
    
    // Hızlı satış ekranlarını ekle
    permittedScreens.forEach(screen => {
      allOptions.push({
        id: screen.id,
        name: `Hızlı Satış: ${screen.name}`,
        type: 'quicksale'
      });
    });
    
    // PYK ekranlarını ekle
    permittedPykScreens.forEach(screen => {
      allOptions.push({
        id: screen.id,
        name: `PYK: ${screen.name}`,
        type: 'pyk',
        branchId: screen.branchId,
        branchName: screen.branchName
      });
    });
    
    // Alfabetik sıralama
    allOptions.sort((a, b) => a.name.localeCompare(b.name));
    
    setAllScreenOptions(allOptions);
  }, [permittedScreens, permittedPykScreens]);

  // Menu render edilmeden önce
  // console.log("[QuickSaleAppBar] Menu Item Koşul Kontrolü:", {
  //   canAddAccount: currentUserPermissions?.canAddAccount,
  //   isAdmin: userDetails?.role === 'admin',
  //   onOpenBulkAddAccountModalExists: !!onOpenBulkAddAccountModal,
  //   handleCloseUserMenuExists: !!handleCloseUserMenu
  // });

  return (
    <AppBar position="static" elevation={1} sx={{ bgcolor: 'primary.main' }}>
      <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
        {/* GERİ BUTONU - Restaurant modunda her zaman göster */}
        {isRestaurantMode ? (
          <IconButton 
            edge="start" 
            color="inherit" 
            onClick={() => {
              // ✅ YENİ: onBackClick varsa önce onu çağır (yazdırılmamış ürün kontrolü için)
              if (onBackClick) {
                onBackClick();
              } else {
                navigate(-1);
              }
            }} 
            sx={{ mr: 1 }}
          >
            <ArrowBackIcon />
          </IconButton>
        ) : (
          !isMobile && userDetails?.role !== 'employee' && (
            <IconButton edge="start" color="inherit" onClick={() => navigate(-1)} sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
          )
        )}
        
        {/* İKON ve BAŞLIK - Restaurant modunda mobilde gizle */}
        {!(isRestaurantMode && isMobile) && (
          <>
            {displayTitle.includes('PYK') ? (
              <AccountBalanceWalletIcon sx={{ mr: 1, fontSize: '1.5rem' }} />
            ) : (
              <PointOfSaleIcon sx={{ mr: 1, fontSize: '1.5rem' }} />
            )}
            
            <Typography variant="h6" component="div" sx={{ fontSize: '1.1rem', mr: 2, display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
              <span>{displayTitle}</span>
              {/* ✅ Sürüm numarası (package.json'dan otomatik) */}
              <Box
                component="span"
                sx={{ fontSize: '0.7rem', opacity: 0.7, fontWeight: 500, letterSpacing: 0.3 }}
              >
                v{APP_VERSION}
              </Box>
              {/* "(Ayarlar: ...)" zaman damgası header'dan kaldırıldı (kullanıcı isteği). lastSettingsRefresh prop'u sessizce yok sayılır. */}
            </Typography>
          </>
        )}

        {/* ✅ RESTAURANT MODU: Masa Adı + Garson Adı
            Desktop bloğu artık Spacer'dan sonra sağa kaydırıldı (kullanıcı isteği).
            Mobil bloğu burada (kendi flexGrow spacer'ı ile sağa hizalanıyor). */}
        {isRestaurantMode && tableName && waiterName && (
          <>
            {/* Mobil: Sağa dayalı — desktop'a yakın boyut, ekrana sığacak şekilde biraz küçük */}
            {isMobile && (
              <>
                <Box sx={{ flexGrow: 1 }} /> {/* Spacer - chip'leri sağa iter */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.75,
                  mr: 1, // Menü butonundan biraz boşluk
                }}>
                  <Chip 
                    label={tableName} 
                    color="warning" 
                    variant="filled" 
                    sx={{ 
                      bgcolor: 'warning.main', 
                      color: 'white', 
                      fontWeight: 700, 
                      fontSize: '0.85rem',
                      height: 34,
                      borderRadius: 1.5,
                      '& .MuiChip-label': { px: 1.25 },
                    }} 
                  />
                  <Chip 
                    label={waiterName} 
                    color="success" 
                    variant="filled" 
                    sx={{ 
                      bgcolor: 'success.main', 
                      color: 'white', 
                      fontWeight: 700, 
                      fontSize: '0.85rem',
                      height: 34,
                      borderRadius: 1.5,
                      '& .MuiChip-label': { px: 1.25 },
                    }} 
                  />
                </Box>
              </>
            )}
          </>
        )}

        {/* ✅ NORMAL MOD (non-restaurant): Kasiyer/Garson + HS Numarası
            Eskiden absolute ile ortada idi; kullanıcı isteğiyle Spacer sonrası sağa kaydırıldı.
            (İlgili JSX aşağıdaki "Sağa hizalı bilgi blokları" bölümünde.) */}

        {/* Tüm Ekranlar Filtreleme - KALDIRILDI (kullanıcı isteği üzerine gizlendi) */}
        {false && !isMobile && !isRestaurantMode && !isPackageMode && allScreenOptions.length > 1 && (
          <Autocomplete
            options={allScreenOptions}
            getOptionLabel={(option) => option.name}
            value={selectedScreenOption || undefined}
            onChange={(event, newValue) => {
              if (newValue) {
                setSelectedScreenOption(newValue);
                if (newValue.type === 'quicksale') {
                navigate(`/hizli-satis/ekran/${newValue.id}`);
                } else {
                  navigate(`/pyk-ekrani/${newValue.id}`);
                }
              }
            }}
            size="small"
            disableClearable
            loading={loadingPykScreens}
            sx={{
              minWidth: 250,
              maxWidth: 350,
              flexGrow: 1,
              '& .MuiOutlinedInput-root': {
                bgcolor: 'background.paper',
                borderRadius: 1,
                height: 36,
              },
              '& .MuiAutocomplete-input': {
                py: 0.5,
              },
            }}
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ 
                py: 0.5, 
                bgcolor: option.type === 'pyk' ? 'rgba(25, 118, 210, 0.08)' : 'inherit'
              }}>
                {option.type === 'pyk' ? (
                  <AccountBalanceWalletIcon fontSize="small" sx={{ mr: 1, opacity: 0.7 }} />
                ) : (
                  <PointOfSaleIcon fontSize="small" sx={{ mr: 1, opacity: 0.7 }} />
                )}
                <Typography variant="body2">
                  {option.type === 'pyk' ? option.name.replace('PYK: ', '') : option.name.replace('Hızlı Satış: ', '')}
                </Typography>
              </Box>
            )}
            renderInput={(params) => (
              <TextField 
                {...params} 
                variant="outlined" 
                placeholder="Ekran Seçin" 
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loadingPykScreens ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
            groupBy={(option) => option.type === 'quicksale' ? 'Hızlı Satış Ekranları' : 'PYK Ekranları'}
          />
        )}

        {/* Spacer / Inline Duyuru Marquee
            - Aktif duyuru yoksa: klasik flexGrow:1 spacer.
            - Varsa: aynı genişlikte alanı kaplayan, beyaz, şeffaf zeminli kayan duyuru bandı.
            - Mobil restaurant modunda chip'lerin kendi iç spacer'ı olduğu için burayı render etmiyoruz. */}
        {!(isRestaurantMode && isMobile) && (
          announcements && announcements.length > 0 ? (
            <AnnouncementMarquee
              announcements={announcements}
              inline
              speedMsPerPx={15}
            />
          ) : (
            <Box sx={{ flexGrow: 1 }} />
          )
        )}

        {/* ✨ SAĞA HİZALI BİLGİ BLOKLARI (desktop)
            Eskiden absolute ile ortada idi; kullanıcı isteğiyle sağ butonların hemen soluna
            kaydırıldı. Mobil restaurant kendi bloğunu yukarıda render ediyor (atla). */}

        {/* DESKTOP — RESTAURANT MOD: Masa Adı + Garson Adı (Adisyon No aşağıda ayrıca) */}
        {!isMobile && isRestaurantMode && tableName && waiterName && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mr: 1.5 }}>
            <Chip
              label={tableName}
              color="warning"
              variant="filled"
              sx={{
                bgcolor: 'warning.main',
                color: 'white',
                fontWeight: 700,
                fontSize: '0.95rem',
                height: 40,
                borderRadius: 1.5,
                '& .MuiChip-label': { px: 2 },
              }}
            />
            <Chip
              label={waiterName}
              color="success"
              variant="filled"
              sx={{
                bgcolor: 'success.main',
                color: 'white',
                fontWeight: 700,
                fontSize: '0.95rem',
                height: 40,
                borderRadius: 1.5,
                '& .MuiChip-label': { px: 2 },
              }}
            />
          </Box>
        )}

        {/* DESKTOP — NORMAL MOD: Kasiyer/Garson + HS (Sipariş) Numarası */}
        {!isMobile && !isRestaurantMode && (reservedShortCode || (useCashierSystem && (currentCashierName || onOpenCashierLogin))) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mr: 1.5 }}>
            {/* Kasiyer / Garson */}
            {useCashierSystem && (
              currentCashierName ? (
                <Chip
                  icon={<PersonIcon sx={{ fontSize: '1.15rem !important', color: 'white !important' }} />}
                  label={currentCashierName}
                  variant="filled"
                  onClick={onCashierLogout}
                  sx={{
                    bgcolor: 'success.main',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    height: 40,
                    borderRadius: 1.5,
                    cursor: 'pointer',
                    '& .MuiChip-label': { px: 1.5 },
                    '& .MuiChip-icon': { color: 'white', ml: 1 },
                    '&:hover': { bgcolor: 'success.dark' },
                  }}
                />
              ) : (
                onOpenCashierLogin && (
                  <Tooltip title="Kasiyer Girişi" placement="bottom">
                    <Button
                      variant="contained"
                      color="warning"
                      startIcon={<LoginIcon sx={{ fontSize: '1.25rem !important' }} />}
                      onClick={onOpenCashierLogin}
                      sx={{
                        fontWeight: 700,
                        height: 40,
                        px: 2,
                        fontSize: '0.95rem',
                        textTransform: 'none',
                        borderRadius: 1.5,
                      }}
                    >
                      Kasiyer Giriş
                    </Button>
                  </Tooltip>
                )
              )
            )}

            {/* HS / Sipariş Numarası */}
            {reservedShortCode && (
              <Tooltip title="Sipariş Numarası (Sonraki satış için ayrıldı)" placement="bottom">
                <Chip
                  label={reservedShortCode}
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.22)',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    height: 40,
                    borderRadius: 1.5,
                    '& .MuiChip-label': { px: 2 },
                  }}
                />
              </Tooltip>
            )}
          </Box>
        )}

        {/* DESKTOP — RESTAURANT: Adisyon Numarası (table/waiter chip'lerinin sağında)
            Mobil değilken her zaman göster; mobil zaten chip bloğunda göstermiyor (eski davranış korunuyor). */}
        {!isMobile && reservedShortCode && isRestaurantMode && (
          <Tooltip title="Adisyon Numarası" placement="bottom">
            <Chip
              label={reservedShortCode}
              sx={{
                bgcolor: 'rgba(255, 193, 7, 0.25)',
                color: 'white',
                fontWeight: 700,
                fontSize: '0.95rem',
                height: 40,
                borderRadius: 1.5,
                mr: 1,
                border: '1px solid rgba(255, 193, 7, 0.5)',
                '& .MuiChip-label': { px: 2 },
              }}
            />
          </Tooltip>
        )}

        {/* SAĞ TARAF BUTONLARI - MOBİLDE GİZLE */}
        {!isMobile && (
          <>
        {/* ℹ Kasiyer giriş/bilgi alanı normal modda artık header ortasında gösteriliyor (yukarıda).
            Buradaki sağ-taraf bloğu kaldırıldı (kullanıcı isteği). */}

        {/* TAM EKRAN BUTONU - Büyütüldü (kullanıcı isteği) */}
        <Tooltip title={isFullscreen ? t('quickSaleAppBar.exitFullscreen') : t('quickSaleAppBar.fullscreen')} placement="bottom">
          <IconButton
            color="inherit"
            onClick={toggleFullscreen}
            size="large"
            sx={{ mr: 1, p: 1.25 }}
          >
            {isFullscreen
              ? <FullscreenExitIcon sx={{ fontSize: '1.85rem' }} />
              : <FullscreenIcon sx={{ fontSize: '1.85rem' }} />}
          </IconButton>
        </Tooltip>

        {/* Yazarkasa (ÖKC) Bağlantı Durumu Göstergesi - Inpos veya Beko seçiliyse */}
        {useFiscalDeviceForCash && selectedFiscalDevice && (selectedFiscalDevice.startsWith('inpos') || selectedFiscalDevice === 'beko_x30tr' || selectedFiscalDevice === 'beko_300tr') && fiscalDeviceStatus !== 'not_configured' && (
          <Tooltip 
            title={
              (() => {
                const deviceName = selectedFiscalDevice === 'hugin_t300' ? 'Hugin T300'
                  : selectedFiscalDevice === 'beko_x30tr' ? 'Beko X30TR'
                  : selectedFiscalDevice === 'beko_300tr' ? 'Beko 300TR'
                  : 'Inpos M530';
                return fiscalDeviceStatus === 'connected'
                  ? `${deviceName} Bağlı`
                  : fiscalDeviceStatus === 'checking'
                  ? `${deviceName} Kontrol Ediliyor...`
                  : `${deviceName} Bağlı Değil`;
              })()
            } 
            placement="bottom"
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                mr: 1,
                borderRadius: 1,
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                cursor: 'help',
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor:
                    fiscalDeviceStatus === 'connected'
                      ? '#4caf50'
                      : fiscalDeviceStatus === 'checking'
                      ? '#ff9800'
                      : '#f44336',
                  animation:
                    fiscalDeviceStatus === 'checking'
                      ? 'pulse 1.5s ease-in-out infinite'
                      : 'none',
                  '@keyframes pulse': {
                    '0%, 100%': {
                      opacity: 1,
                    },
                    '50%': {
                      opacity: 0.3,
                    },
                  },
                }}
              />
              {!isMobile && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'white',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                  }}
                >
                  {selectedFiscalDevice === 'hugin_t300' ? 'Hugin'
                    : selectedFiscalDevice === 'beko_x30tr' ? 'Beko X30TR'
                    : selectedFiscalDevice === 'beko_300tr' ? 'Beko 300TR'
                    : 'Inpos'}
                  {fiscalDeviceStatus === 'connected' ? ' ✓' : fiscalDeviceStatus === 'checking' ? '...' : ' ✗'}
                </Typography>
              )}
            </Box>
          </Tooltip>
        )}

        {/* ✅ YENİ: SignalR Bağlantı Durumu Göstergesi (SADECE Mutfak Ekranı Açıksa) */}
        {useKitchenScreen && (
          <Tooltip 
            title={
              signalRStatus === signalR.HubConnectionState.Connected
                ? "Mutfak Siparişlerini Görüntüle 🍽️"
                : signalRStatus === signalR.HubConnectionState.Connecting
                ? "Mutfak Ekranı Bağlanıyor... 🟡"
                : signalRStatus === signalR.HubConnectionState.Reconnecting
                ? "Mutfak Ekranı Yeniden Bağlanıyor... 🟡"
                : "Mutfak Ekranı Bağlı Değil 🔴"
            } 
            placement="bottom"
          >
            <Box
              onClick={onOpenKitchenOrdersModal}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 2,
                py: 1,
                mr: 1,
                borderRadius: 1.5,
                bgcolor: 'rgba(255, 255, 255, 0.12)',
                cursor: onOpenKitchenOrdersModal ? 'pointer' : 'default',
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: onOpenKitchenOrdersModal ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.2)',
                  transform: onOpenKitchenOrdersModal ? 'scale(1.04)' : 'none',
                },
              }}
            >
              <Box
                sx={{
                  width: 13,
                  height: 13,
                  borderRadius: '50%',
                  bgcolor:
                    signalRStatus === signalR.HubConnectionState.Connected
                      ? '#4caf50' // Yeşil
                      : signalRStatus === signalR.HubConnectionState.Connecting ||
                        signalRStatus === signalR.HubConnectionState.Reconnecting
                      ? '#ff9800' // Turuncu
                      : '#f44336', // Kırmızı
                  animation:
                    signalRStatus === signalR.HubConnectionState.Connecting ||
                    signalRStatus === signalR.HubConnectionState.Reconnecting
                      ? 'pulse 1.5s ease-in-out infinite'
                      : 'none',
                  '@keyframes pulse': {
                    '0%, 100%': {
                      opacity: 1,
                    },
                    '50%': {
                      opacity: 0.3,
                    },
                  },
                }}
              />
              {!isMobile && (
                <Typography
                  sx={{
                    color: 'white',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    letterSpacing: '0.01em',
                  }}
                >
                  {signalRStatus === signalR.HubConnectionState.Connected
                    ? 'Mutfak'
                    : signalRStatus === signalR.HubConnectionState.Connecting ||
                      signalRStatus === signalR.HubConnectionState.Reconnecting
                    ? 'Bağlanıyor'
                    : 'Bağlı Değil'}
                </Typography>
              )}
            </Box>
          </Tooltip>
        )}

        {/* ℹ Eskiden burada bağımsız "Ekran Arayüz Ayarları" (Settings) IconButton vardı.
            Artık aşağıdaki "Menü" dropdown'ının içine MenuItem olarak taşındı (kullanıcı isteği). */}

        {/* ✅ MENÜ BUTONU — eski profil (PersonIcon) butonunun yerine geçer.
            İçinde: Ekran Arayüz Ayarları, Ürün Kartı Düzeni, Güvenli Çıkış. */}
        {handleOpenUserMenu && (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title={userFullName || t('quickSaleAppBar.menu')}>
            <Button
              onClick={handleOpenUserMenu}
              startIcon={<MenuIcon sx={{ fontSize: '1.5rem !important' }} />}
              sx={{
                bgcolor: 'white',
                color: 'primary.dark',
                px: 2,
                py: 0.75,
                fontWeight: 700,
                textTransform: 'none',
                fontSize: '0.95rem',
                borderRadius: 1.5,
                minHeight: 40,
                '&:hover': {
                  bgcolor: 'primary.dark',
                  color: 'white',
                },
              }}
            >
              {t('quickSaleAppBar.menu')}
            </Button>
          </Tooltip>
            {anchorElUserMenu && handleCloseUserMenu && (
          <Menu
            anchorEl={anchorElUserMenu}
            open={Boolean(anchorElUserMenu)}
            onClose={handleCloseUserMenu}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            MenuListProps={{ sx: { py: 0.5, minWidth: 220 } }}
          >
            {handleOpenUISettingsModal && (
              <MenuItem onClick={() => { handleCloseUserMenu(); handleOpenUISettingsModal(); }} sx={{ py: 1, px: 2 }}>
                <SettingsIcon sx={{ mr: 1.5, fontSize: '1.2rem' }} />
                <Typography variant="body2">{t('quickSaleAppBar.screenUiSettings')}</Typography>
              </MenuItem>
            )}
            {onOpenProductSortSettings && (
              <MenuItem onClick={() => { handleCloseUserMenu(); onOpenProductSortSettings(); }} sx={{ py: 1, px: 2 }}>
                <GridViewIcon sx={{ mr: 1.5, fontSize: '1.2rem' }} />
                <Typography variant="body2">{t('quickSaleAppBar.productCardLayout')}</Typography>
              </MenuItem>
            )}
            {onOpenEndOfDayPrint && currentUserPermissions?.canViewEndOfDay && (
              <MenuItem onClick={() => { handleCloseUserMenu && handleCloseUserMenu(); onOpenEndOfDayPrint(); }} sx={{ py: 1, px: 2 }}>
                <PrintIcon sx={{ mr: 1.5, fontSize: '1.2rem' }} />
                <Typography variant="body2">{t('quickSaleAppBar.endOfDay', 'Gün Sonu')}</Typography>
              </MenuItem>
            )}
            {/* 🖥️ Plan 2 — Kiosk Ürünleri */}
            {onOpenKioskProducts && (
              <MenuItem onClick={() => { handleCloseUserMenu && handleCloseUserMenu(); onOpenKioskProducts(); }} sx={{ py: 1, px: 2 }}>
                <Inventory2Icon sx={{ mr: 1.5, fontSize: '1.2rem' }} />
                <Typography variant="body2">{t('quickSaleAppBar.kioskProducts')}</Typography>
              </MenuItem>
            )}
            {handleLogout && (handleOpenUISettingsModal || onOpenProductSortSettings || (onOpenEndOfDayPrint && currentUserPermissions?.canViewEndOfDay) || onOpenKioskProducts) && (
              <Divider sx={{ my: 0.5 }} />
            )}
            {handleLogout && (
              <MenuItem onClick={handleLogout} sx={{ py: 1, px: 2, color: 'error.main' }}>
                <LogoutIcon sx={{ mr: 1.5, fontSize: '1.2rem' }} />
                <Typography variant="body2">{t('quickSaleAppBar.safeLogout')}</Typography>
              </MenuItem>
            )}
          </Menu>
            )}
        </Box>
        )}
          </>
        )}

        {/* MOBİL MENÜ BUTONU - Sağ tarafta, sadece mobilde göster */}
        {isMobile && (
          <IconButton 
            edge="end"
            color="inherit" 
            onClick={() => setMobileMenuOpen(true)} 
            sx={{ ml: 'auto' }}
          >
            <MenuIcon />
          </IconButton>
        )}
      </Toolbar>

      {/* ℹ Duyuru marquee artık ayrı bir alt-şerit DEĞİL; Toolbar içinde başlık ile sağ chip'ler
          arasındaki boşlukta inline olarak akıyor (kullanıcı isteği). */}

      {/* MOBİL MENÜ DRAWER - Sağdan sola açılır */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            bgcolor: 'background.paper',
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* ✅ YENİ: Kullanıcı Bilgisi - Sadece ikon ve rol (isim soyisim kaldırıldı) */}
          <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
              }}
            >
              <PersonIcon />
            </Box>
            {userDetails?.role && (
              <Typography variant="body2" color="text.secondary">
                {userDetails.role === 'admin' ? t('quickSaleAppBar.admin') : t('quickSaleAppBar.staff')}
              </Typography>
            )}
          </Box>

          {/* Menü Butonları */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Geri Butonu */}
            {(userDetails?.role !== 'employee' || isRestaurantMode) && (
              <MenuItem 
                onClick={() => {
                  setMobileMenuOpen(false);
                  // ✅ YENİ: Restaurant modunda onBackClick varsa onu çağır
                  if (isRestaurantMode && onBackClick) {
                    onBackClick();
                  } else {
                    navigate(-1);
                  }
                }}
                sx={{ borderRadius: 1 }}
              >
                <ListItemIcon>
                  <ArrowBackIcon />
                </ListItemIcon>
                <ListItemText>{t('quickSaleAppBar.back')}</ListItemText>
              </MenuItem>
            )}

            {/* Tam Ekran */}
            <MenuItem 
              onClick={() => {
                toggleFullscreen();
                setMobileMenuOpen(false);
              }}
              sx={{ borderRadius: 1 }}
            >
              <ListItemIcon>
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </ListItemIcon>
              <ListItemText>{isFullscreen ? t('quickSaleAppBar.exitFullscreen') : t('quickSaleAppBar.fullscreen')}</ListItemText>
            </MenuItem>

            {/* ✅ Kasiyer Giriş/Çıkış (Mobil - SADECE NORMAL MOD, restoran modunda zaten başka yerde gösteriliyor) */}
            {useCashierSystem && !isRestaurantMode && (
              currentCashierName ? (
                <MenuItem 
                  onClick={() => {
                    onCashierLogout?.();
                    setMobileMenuOpen(false);
                  }}
                  sx={{ borderRadius: 1 }}
                >
                  <ListItemIcon>
                    <PersonIcon sx={{ color: 'success.main' }} />
                  </ListItemIcon>
                  <ListItemText>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                        {currentCashierName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        (Çıkış için dokun)
                      </Typography>
                    </Box>
                  </ListItemText>
                </MenuItem>
              ) : (
                <MenuItem 
                  onClick={() => {
                    onOpenCashierLogin?.();
                    setMobileMenuOpen(false);
                  }}
                  sx={{ borderRadius: 1, bgcolor: 'warning.light' }}
                >
                  <ListItemIcon>
                    <LoginIcon sx={{ color: 'warning.dark' }} />
                  </ListItemIcon>
                  <ListItemText sx={{ color: 'warning.dark', fontWeight: 600 }}>Kasiyer Giriş</ListItemText>
                </MenuItem>
              )
            )}

            {/* Yeni Cari Kayıt */}
            {(currentUserPermissions?.canAddAccount || userDetails?.role === 'admin') && onOpenBulkAddAccountModal && (
              <MenuItem 
                onClick={() => {
                  onOpenBulkAddAccountModal();
                  setMobileMenuOpen(false);
                }}
                sx={{ borderRadius: 1 }}
              >
                <ListItemIcon>
                  <PersonAddAlt1Icon />
                </ListItemIcon>
                <ListItemText>{t('quickSaleAppBar.newAccount')}</ListItemText>
              </MenuItem>
            )}

            {/* Ekran Arayüz Ayarları */}
            {handleOpenUISettingsModal && (
              <MenuItem 
                onClick={() => {
                  handleOpenUISettingsModal();
                  setMobileMenuOpen(false);
                }}
                sx={{ borderRadius: 1 }}
              >
                <ListItemIcon>
                  <SettingsIcon />
                </ListItemIcon>
                <ListItemText>{t('quickSaleAppBar.screenSettings')}</ListItemText>
              </MenuItem>
            )}

            {/* Ürün Kartı Düzeni */}
            {onOpenProductSortSettings && (
              <MenuItem 
                onClick={() => {
                  onOpenProductSortSettings();
                  setMobileMenuOpen(false);
                }}
                sx={{ borderRadius: 1 }}
              >
                <ListItemIcon>
                  <GridViewIcon />
                </ListItemIcon>
                <ListItemText>{t('quickSaleAppBar.productCardLayout')}</ListItemText>
              </MenuItem>
            )}

            {/* 🖥️ Plan 2 — Kiosk Ürünleri (Mobil Drawer) */}
            {onOpenKioskProducts && (
              <MenuItem
                onClick={() => {
                  onOpenKioskProducts();
                  setMobileMenuOpen(false);
                }}
                sx={{ borderRadius: 1 }}
              >
                <ListItemIcon>
                  <Inventory2Icon />
                </ListItemIcon>
                <ListItemText>{t('quickSaleAppBar.kioskProducts')}</ListItemText>
              </MenuItem>
            )}

            <Divider sx={{ my: 1 }} />

            {/* Güvenli Çıkış */}
            {handleLogout && (
              <MenuItem 
                onClick={() => {
                  handleLogout();
                  setMobileMenuOpen(false);
                }}
                sx={{ borderRadius: 1, color: 'error.main' }}
              >
                <ListItemIcon sx={{ color: 'error.main' }}>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText>{t('quickSaleAppBar.safeLogout')}</ListItemText>
              </MenuItem>
            )}
          </Box>
        </Box>
      </Drawer>
    </AppBar>
  );
};

export default QuickSaleAppBar; 